/**
 * HIFLD Electric Substations & Transmission Lines — grid infrastructure.
 * Free, no API key, ArcGIS REST proximity query.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, gridCellKey, haversineKm } from './types';
import { queryArcgisRest } from '../arcgis/queryArcgisRest';
import type { GridData } from '../../types/siteRag';
import { refdataNear, tryRefdata } from '../refdata/refdataClient';

interface SubstationFeature {
  attributes: {
    NAME?: string;
    MAX_VOLT?: number;
    MIN_VOLT?: number;
    STATUS?: string;
    LINES?: number;
    LATITUDE?: number;
    LONGITUDE?: number;
    CITY?: string;
    STATE?: string;
  };
}

/**
 * RD-4 — grid cache contract version.
 *
 * `withCache` reads Firestore BEFORE calling `provider.fetch`, so every
 * location already in `cache/hifld-grid:{cell}` bypassed the refdata-first
 * lookup entirely — the RD-1 snapshot was unreachable for exactly the sites
 * that had been screened before. Live evidence (2026-08-03): the Redhead
 * cell 30.35_-93.25 was still serving a 2026-07-27 live-path result naming
 * GILLIS at 5.2 km with voltage '0kV', while the snapshot resolves two nearer
 * in-service 230 kV facilities (TAP143264 2.93 km, MOSS BLUFF 3.00 km).
 *
 * Bumping this string retires every pre-snapshot entry. Three semantics
 * changed and none of them can be expressed in the old payloads:
 *   - refdata-first feature acquisition (RD-1)
 *   - real per-line transmission distance, replacing the hardcoded 0/15 (C1)
 *   - voltage sentinel + unit normalisation (RD-2, RD-4)
 */
const GRID_CACHE_CONTRACT_VERSION = 'v2-refdata-snapshot';

export const hifldGridProvider: DataProvider<GridData> = {
  id: 'hifld-grid',
  name: 'HIFLD Electric Substations & Transmission Lines',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${GRID_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<GridData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`hifld-grid` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. The reported substation/transmission distances are
    // haversine'd from this point — they should be measured from the parcel
    // centre, not the geocoded mailbox, so the centroid is the right origin
    // for both the envelope query and the distance computations below.
    // Query substations within ~50km using envelope geometry (more reliable than point+distance)
    const latBuffer = 0.45; // ~50km in latitude degrees
    const lngBuffer = 0.45 / Math.cos(lat * Math.PI / 180); // scale by cos(lat) for uniform distance
    const xmin = lng - lngBuffer, ymin = lat - latBuffer, xmax = lng + lngBuffer, ymax = lat + latBuffer;

    // RD-1 — local-first: the nationwide HIFLD snapshot in GCS (refdata)
    // answers this query without touching the flaky ArcGIS hosts. Only the
    // FEATURE ACQUISITION is swapped — everything downstream (nearest
    // selection, capacity/complexity heuristics, live transmission
    // enrichment) is shared with the live path so payloads stay identical.
    // A refdata miss/error falls through to the live endpoint loop.
    let features: SubstationFeature[] | null = null;
    let sourceLabel = 'HIFLD';
    const local = await tryRefdata('hifld-substations', () =>
      refdataNear('hifld-substations', { lat, lng }, 50, 20, { notEquals: { STATUS: 'OUT OF SERVICE' } }));
    if (local) {
      sourceLabel = `HIFLD (local snapshot, vintage ${local.vintageDate})`;
      features = local.hits.map(({ feature }) => {
        const num = (v: string | number | null | undefined): number | undefined =>
          typeof v === 'number' && Number.isFinite(v) ? v : undefined;
        const str = (v: string | number | null | undefined): string | undefined =>
          typeof v === 'string' ? v : undefined;
        const geomCoords = feature.geom.type === 'Point' ? feature.geom.coordinates : null;
        return {
          attributes: {
            NAME: str(feature.props.NAME),
            MAX_VOLT: num(feature.props.MAX_VOLT),
            MIN_VOLT: num(feature.props.MIN_VOLT),
            STATUS: str(feature.props.STATUS),
            LINES: num(feature.props.LINES),
            // Geometry is authoritative; harvested LATITUDE/LONGITUDE attrs
            // are the fallback for degenerate rows.
            LATITUDE: geomCoords ? geomCoords[1] : num(feature.props.LATITUDE),
            LONGITUDE: geomCoords ? geomCoords[0] : num(feature.props.LONGITUDE),
            CITY: str(feature.props.CITY),
            STATE: str(feature.props.STATE),
          },
        };
      });
    }

    if (!features) {
      // Live path — try multiple HIFLD endpoints (service has migrated multiple times)
      const endpoints = [
        `https://services5.arcgis.com/HDRa0B57OVrv2E1q/ArcGIS/rest/services/Electric_Substations/FeatureServer/0/query`,
        `https://services1.arcgis.com/Hp6G80Pky0om6HgB/ArcGIS/rest/services/Electric_Substations_1/FeatureServer/0/query`,
        `https://services1.arcgis.com/Hp6G80Pky0om6HgB/arcgis/rest/services/Electric_Substations/FeatureServer/0/query`,
        `https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Electric_Substations_1/FeatureServer/0/query`,
      ];

      const params = `?geometry=${xmin},${ymin},${xmax},${ymax}&geometryType=esriGeometryEnvelope` +
        `&inSR=4326&spatialRel=esriSpatialRelIntersects` +
        `&outFields=NAME,MAX_VOLT,MIN_VOLT,STATUS,LINES,LATITUDE,LONGITUDE,CITY,STATE` +
        `&resultRecordCount=20&f=json`;

      let json: any = null;
      for (const endpoint of endpoints) {
        try {
          const data = await queryArcgisRest(endpoint + params, {
            label: 'HIFLD substations',
            timeoutMs: 15_000,
            signal: geo?.signal,
          });
          if (data.features && data.features.length > 0) { json = data; break; }
          if (data.error) continue;
          json = data;
          break;
        } catch { continue; }
      }

      if (!json) {
        // Do not invent distance/capacity — orchestrator marks grid unavailable.
        throw new Error('HIFLD substation service unavailable — no verified grid data for this location');
      }

      features = ((json.features || []) as SubstationFeature[]).filter(
        (f: SubstationFeature) => f.attributes?.STATUS !== 'OUT OF SERVICE',
      );
    }

    if (features.length === 0) {
      throw new Error('HIFLD — no in-service substations found within the 50 km search envelope');
    }

    // RD-2 — HIFLD voltage-unit drift. The pre-2026-05 hosts reported
    // MAX_VOLT/MIN_VOLT/VOLTAGE in VOLTS (230000); the current services5
    // host reports KILOVOLTS (230). The thresholds/labels below assume
    // volts, so kV-unit rows classified as `(230/1000)="0kV"` + capacity
    // 'Constrained' — live AND snapshot alike (they share this data).
    // Normalise here: plausible substation/line voltages are ≥ 1 kV and
    // < 1000 kV, so any positive value < 1000 is kV → convert to volts.
    // RD-4 — HIFLD encodes "voltage not known" as the sentinel -999999 rather
    // than null (confirmed present in both the live service and the snapshot).
    // Treating it as a number rendered a confident `-1000kV` into live SEDs
    // (cache doc hifld-grid:32.77_-111.59, 2026-07-27). Any non-positive value
    // is not a voltage — drop it to undefined so the label falls back to
    // 'Unknown' instead of asserting a fabricated figure.
    const normalizeVoltage = (v: number | undefined): number | undefined => {
      if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return undefined;
      return v < 1000 ? v * 1000 : v;
    };
    for (const f of features) {
      f.attributes.MAX_VOLT = normalizeVoltage(f.attributes.MAX_VOLT);
      f.attributes.MIN_VOLT = normalizeVoltage(f.attributes.MIN_VOLT);
    }

    // Find nearest substation by distance
    let nearest = features[0];
    let nearestDist = Infinity;
    for (const f of features) {
      const sLat = f.attributes.LATITUDE ?? 0;
      const sLng = f.attributes.LONGITUDE ?? 0;
      if (sLat === 0 && sLng === 0) continue;
      const d = haversineKm(lat, lng, sLat, sLng);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = f;
      }
    }

    const substationDist = parseFloat(nearestDist.toFixed(1));
    const maxVolt = nearest.attributes.MAX_VOLT ?? 0;
    // RD-4 — an unknown/sentinel voltage must read 'Unknown', never a derived
    // number. `${(maxVolt / 1000)}kV` on a 0 or sentinel produced '0kV' and
    // '-1000kV' in production SEDs; both were consumed downstream as fact.
    const voltage = maxVolt >= 345000 ? '345kV'
      : maxVolt >= 230000 ? '230kV'
      : maxVolt >= 115000 ? '115kV'
      : maxVolt >= 69000 ? '69kV'
      : maxVolt > 0 ? `${(maxVolt / 1000).toFixed(0)}kV`
      : 'Unknown';

    // Capacity heuristic based on voltage and count
    const highVoltCount = features.filter(f => (f.attributes.MAX_VOLT ?? 0) >= 230000).length;
    const capacity: GridData['capacity'] = highVoltCount >= 3 ? 'High' : highVoltCount >= 1 ? 'Limited' : 'Constrained';

    // Complexity based on distance and voltage
    const complexity: GridData['complexity'] = substationDist < 3 && maxVolt >= 115000 ? 'Low'
      : substationDist < 10 ? 'Medium' : 'High';

    const nearestName = nearest.attributes.NAME || 'Unknown';
    const nearestCity = nearest.attributes.CITY || '';
    const nearestState = nearest.attributes.STATE || '';

    // Also fetch transmission lines within ~30km for grid study
    let transmissionLines: { voltage: string; distKm: number; owner?: string }[] = [];
    let nearestTransmissionKm: number | undefined;
    let nearestTransmissionVoltage: string | undefined;
    // Source-string voltage summary. Live semantics: the MAX voltage nearby
    // (that is what the legacy nearestTransmissionVoltage held). The refdata
    // branch keeps the max in the human-readable string while upgrading
    // nearestTransmissionVoltage itself to the NEAREST line's voltage.
    let txVoltageSummary: string | undefined;

    // RD-1 (C1) — local-first transmission with REAL geometry distances.
    // The live path never computed line distance (distKm was hardcoded 0 and
    // nearestTransmissionKm a hardcoded 15 km guess); the clipped snapshot
    // geometry gives true point-to-line distances.
    const kvLabel = (v: string | number | null | undefined): string => {
      if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return 'Unknown';
      const volts = normalizeVoltage(v)!;
      return `${(volts / 1000).toFixed(0)}kV`;
    };
    const localTx = await tryRefdata('hifld-transmission', () =>
      refdataNear('hifld-transmission', { lat, lng }, 30, 15));
    if (localTx) {
      if (localTx.hits.length > 0) {
        transmissionLines = localTx.hits.slice(0, 10).map(h => ({
          voltage: kvLabel(h.feature.props.VOLTAGE),
          distKm: parseFloat(h.distanceKm.toFixed(1)),
          owner: typeof h.feature.props.OWNER === 'string' && h.feature.props.OWNER !== ''
            ? h.feature.props.OWNER : undefined,
        }));
        nearestTransmissionKm = parseFloat(localTx.hits[0].distanceKm.toFixed(1));
        nearestTransmissionVoltage = kvLabel(localTx.hits[0].feature.props.VOLTAGE);
        // RD-2 — normalise before max() so kV-unit rows compare correctly.
        const maxVolt = Math.max(...localTx.hits.map(h =>
          typeof h.feature.props.VOLTAGE === 'number' ? normalizeVoltage(h.feature.props.VOLTAGE) ?? 0 : 0));
        txVoltageSummary = maxVolt > 0 ? `${(maxVolt / 1000).toFixed(0)}kV` : nearestTransmissionVoltage;
      }
      // Authoritative empty: no lines within 30 km — same shape as live-empty.
    } else {
      try {
        await fetchTransmissionLive();
      } catch { /* transmission line fetch is best-effort */ }
      txVoltageSummary = nearestTransmissionVoltage;
    }

    async function fetchTransmissionLive(): Promise<void> {
      const txBuffer = 0.27; // ~30km
      const txParams = `?geometry=${lng - txBuffer},${lat - txBuffer},${lng + txBuffer},${lat + txBuffer}&geometryType=esriGeometryEnvelope` +
        `&inSR=4326&spatialRel=esriSpatialRelIntersects` +
        `&outFields=VOLTAGE,OWNER,SUB_1,SUB_2,SHAPE_Length` +
        `&resultRecordCount=15&f=json`;
      const txEndpoints = [
        'https://services5.arcgis.com/HDRa0B57OVrv2E1q/ArcGIS/rest/services/Electric_Power_Transmission_Lines/FeatureServer/0/query',
        'https://services1.arcgis.com/Hp6G80Pky0om6HgB/ArcGIS/rest/services/Electric_Power_Transmission_Lines/FeatureServer/0/query',
        'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Electric_Power_Transmission_Lines/FeatureServer/0/query',
      ];
      for (const ep of txEndpoints) {
        try {
          const txData = await queryArcgisRest(ep + txParams, {
            label: 'HIFLD transmission lines',
            timeoutMs: 12_000,
            signal: geo?.signal,
          });
          const txFeatures: any[] = Array.isArray(txData.features) ? txData.features : [];
          if (txFeatures.length > 0) {
            // RD-2 — normalise the kV-vs-volts unit drift on the live path too.
            transmissionLines = txFeatures.slice(0, 10).map((f: any) => ({
              voltage: kvLabel(f.attributes?.VOLTAGE),
              distKm: 0, // Approximate — line is within 30km envelope
              owner: f.attributes?.OWNER || undefined,
            }));
            // Estimate nearest transmission by highest voltage nearby
            const sortedTx = [...txFeatures].sort((a: any, b: any) =>
              (normalizeVoltage(b.attributes?.VOLTAGE) ?? 0) - (normalizeVoltage(a.attributes?.VOLTAGE) ?? 0));
            if (sortedTx.length > 0) {
              nearestTransmissionVoltage = kvLabel(sortedTx[0].attributes?.VOLTAGE);
              nearestTransmissionKm = 15; // Within envelope, approximate midpoint
            }
            break;
          }
        } catch { continue; }
      }
    }

    // Screening heuristics from infrastructure proximity/voltage, not utility studies or observed load queues.
    const estimatedInterconnectionCost = substationDist < 5 ? '$1-3M' :
      substationDist < 15 ? '$3-8M' : substationDist < 30 ? '$8-20M' : '$15-40M+';
    const estimatedQueueTime = capacity === 'High' ? '12-24 months' :
      capacity === 'Limited' ? '18-36 months' : '24-48 months';

    return {
      data: {
        substationDist, capacity, complexity, voltage,
        nearestSubstationName: nearestName,
        substationsWithin50km: features.length,
        transmissionLines: transmissionLines.length > 0 ? transmissionLines : undefined,
        nearestTransmissionKm,
        nearestTransmissionVoltage,
        estimatedInterconnectionCost,
        estimatedInterconnectionCostBasis: 'proximity-heuristic',
        estimatedQueueTime,
        estimatedQueueTimeBasis: 'proximity-heuristic',
      },
      sources: [{
        field: 'grid',
        source: `${sourceLabel} — nearest substation: ${nearestName} (${nearestCity}, ${nearestState}), ${voltage}, ${substationDist}km away. ${features.length} substations within 50km.${transmissionLines.length > 0 ? ` ${transmissionLines.length} transmission lines within 30km (max ${txVoltageSummary}).` : ''}`,
        url: 'https://hifld-geoplatform.opendata.arcgis.com/datasets/electric-substations',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
